/**
 * Adapter — `ExtractionResult` (watchkeeper-extractors) → `ScrapedWatchEntry[]`
 * (BulkImport's input shape).
 *
 * The extractor emits RAW STRUCTURED DATA and nothing else: every scalar is a
 * verbatim page string ("40mm", "60 hours"), no unit parsing, no lookup-slug
 * canonicalisation, no doc-id minting. That is deliberately the same contract
 * ScrapedWatchEntry has, so this adapter is a rename/reshape pass only — all
 * parsing stays where it already lives (mapEntryToReference and the rules /
 * calibre-check / AI-enrich stages downstream).
 *
 * Deliberately dropped, because ScrapedWatchEntry has no home for them:
 *   - `rawSpecs`  — the lossless label/value catch-all; the mapped fields above
 *                   are the curated subset the importer actually consumes.
 *   - `calibre`   — the nested ExtractedCalibre from a dedicated calibre page.
 *                   The calibre STRING still flows through via `movement`, and
 *                   the wizard's calibre-check step resolves it against the
 *                   calibres collection anyway (which is richer than the scrape).
 *                   The envelope's top-level `calibres[]` IS consumed, but by
 *                   the sibling `extractionToCalibres` adapter, which offers it
 *                   as a pre-filled create form when a lookup comes up empty.
 *   - `frequency` — no ScrapedWatchEntry field; movement.frequencyVph is
 *                   populated from the linked calibre doc, not the scrape.
 * Envelope fields (`jobId`, `stats`, `errors`, `brandInfo`, timestamps) are
 * stripped by extractionResultToEntries — Phase 2 may persist them.
 */
import type {
  ExtractionResult,
  ExtractedWatch,
  TaggedImage,
} from '@watchkeeper/shared-types';
// Type-only, so this is erased at build time and creates no runtime import
// cycle with BulkImport (which imports the functions below).
import type { ScrapedWatchEntry } from '../BulkImport';

/**
 * Structural check for the extractor's result envelope, used to tell an
 * ExtractionResult apart from the hand-rolled `ScrapedWatchEntry[]` / single
 * entry JSON that the file upload has always accepted.
 *
 * `extractorId` is NOT validated against the ExtractorId union: the union is a
 * shared-types version bump behind the service, and rejecting a result because
 * a newly-registered module hasn't been re-pinned yet would be a worse failure
 * than importing it.
 */
export function isExtractionResult(x: unknown): x is ExtractionResult {
  if (typeof x !== 'object' || x === null || Array.isArray(x)) return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.extractorId === 'string' &&
    typeof o.brandId === 'string' &&
    Array.isArray(o.watches) &&
    typeof o.stats === 'object' &&
    o.stats !== null
  );
}

/** Matches a bare 4-digit year in a plausible watchmaking range. */
const YEAR_RE = /\b(1[6-9]\d{2}|2[01]\d{2})\b/g;

/** Phrases that mean "still in production" — an open-ended range has no end. */
const OPEN_ENDED_RE = /present|current|ongoing|\bnow\b|to date/i;

/**
 * Splits the extractor's free-text `productionYears` ("1969", "1969–1972",
 * "1969 to present") into discrete start/end year strings.
 *
 * Two-digit end years ("1969-72") yield a start only — expanding them means
 * guessing a century, and a wrong end year is worse than an absent one.
 */
export function splitProductionYears(raw?: string): {
  start?: string;
  end?: string;
} {
  const text = (raw ?? '').trim();
  if (!text) return {};
  const years = text.match(YEAR_RE);
  if (!years || years.length === 0) return {};
  return {
    start: years[0],
    end: OPEN_ENDED_RE.test(text) ? undefined : years[1],
  };
}

/** Trims, then collapses '' to undefined so stripEntry can drop the key. */
function clean(v?: string): string | undefined {
  const t = v?.trim();
  return t ? t : undefined;
}

/**
 * The extra product shots, normalized to one shape whichever field carried
 * them.
 *
 * `taggedImages` is the richer form of `images` and wins when present; an
 * extractor that only emits bare URLs still contributes a gallery, just an
 * untagged one. `autoTagged` is recomputed rather than trusted — it is
 * redundant with `tags.length > 0` by construction, and a stale `true` on an
 * empty array would read downstream as "the extractor looked and tagged this"
 * when it did not.
 *
 * Order is preserved (the contract puts the primary shot first) and the
 * importer, not this adapter, decides which of these becomes the hero image and
 * which land in the gallery.
 */
function toTaggedImages(w: ExtractedWatch): TaggedImage[] | undefined {
  const source: Array<{ url?: string; tags?: string[] }> =
    w.taggedImages && w.taggedImages.length > 0
      ? w.taggedImages
      : (w.images ?? []).map((url) => ({ url, tags: [] }));

  const out: TaggedImage[] = [];
  for (const img of source) {
    const url = clean(img?.url);
    if (!url) continue;
    // Tags pass through UNVALIDATED (hence the cast): the admin's gallery
    // vocabulary is enforced once, on the write side, so an extractor tag the
    // admin doesn't know is reported there rather than vanishing here — and so
    // hand-rolled JSON, which never touches this adapter, gets the same
    // treatment.
    const tags = (img?.tags ?? [])
      .map((t) => String(t ?? '').trim())
      .filter(Boolean) as TaggedImage['tags'];
    out.push({ url, tags, autoTagged: tags.length > 0 });
  }
  return out.length > 0 ? out : undefined;
}

/** Drops undefined values so the entry carries only keys that have content. */
function stripEntry(entry: ScrapedWatchEntry): ScrapedWatchEntry {
  return Object.fromEntries(
    Object.entries(entry).filter(([, v]) => v !== undefined),
  ) as unknown as ScrapedWatchEntry;
}

function watchToEntry(w: ExtractedWatch): ScrapedWatchEntry {
  const production = splitProductionYears(w.productionYears);

  return stripEntry({
    reference: w.reference.trim(),
    // ScrapedWatchEntry.model is required, but the extractor contract only
    // guarantees `sourceUrl` and `reference`. Fall back down the same ladder
    // the preview grid reads, ending at the reference so the row is never
    // nameless.
    model: clean(w.model) ?? clean(w.collection) ?? clean(w.productName) ?? w.reference.trim(),

    // Renames — the only two fields whose names differ between the contracts.
    url: clean(w.sourceUrl),
    imgSrc: clean(w.imageUrl),

    // Every shot the extractor found, tags included. The hero image still
    // arrives separately as `imgSrc` (and is normally this array's first
    // element); the importer dedups the two by URL before uploading the rest as
    // gallery images.
    taggedImages: toTaggedImages(w),

    collection: clean(w.collection),
    productName: clean(w.productName),
    description: clean(w.description),

    // Movement
    movement: clean(w.movement),
    // The electronic counterpart of `movement`, NOT a spelling of it: the
    // digital-watch module number, which the wizard's module-check step
    // resolves to an `electronicModules` doc and stamps as `moduleId`.
    //
    // Its absence had a silent failure mode worth naming. `movement` IS the
    // calibre string on this path — the calibre-check step scans it raw and
    // offers match-or-create — so Casio module numbers sent on `movement`,
    // before shared-types had a `module` field, became 81 calibres. The
    // extractor now sends `module` correctly, but with no mapping here the
    // value vanished at the adapter instead: references imported with neither
    // a calibre nor a module, and the module check found nothing to resolve.
    // `rawSpecs` is dropped above, so there was no second route to it either.
    module: clean(w.module),
    // Display/form-category HINT — `ana-digi`, `digital`, `analog` — resolved
    // to a canonical lookup_watch_types slug by resolveWatchType, so the
    // spelling only has to match that lookup's value/displayName/synonyms.
    // Same contract as ExtractedProduct.watchType on the manual path, and an
    // explicit hint outranks deriveWatchTypeHint.
    //
    // It has to outrank it, because the derivation gets Casio wrong twice over:
    // `hasModule` with no `hands` reads as pure-digital, so analog quartz with
    // a module classified as digital, and the ana-digi branch could not rescue
    // it either — that wants hands AND a module, or a text match on
    // "analog-digital" where the source writes "digital-analog". 262 of 434
    // G-Shock references landed on the wrong type.
    watchType: clean(w.watchType),
    movementType: clean(w.movementType),
    jewels: clean(w.jewels),
    powerReserve: clean(w.powerReserve),

    // Case
    caseMaterial: clean(w.caseMaterial),
    caseSize: clean(w.caseSize),
    caseHeight: clean(w.caseHeight),
    caseShape: clean(w.caseShape),
    caseBack: clean(w.caseBack),
    lugWidth: clean(w.lugWidth),
    lugToLug: clean(w.lugToLug),
    waterRes: clean(w.waterRes),
    crystal: clean(w.crystal),
    glass: clean(w.glass),
    // ExtractedWatch carries both a generic `bezel` and a `bezelType`. When
    // only the generic one is present it IS the type on the sources that label
    // it that way, so it stays a fallback rather than a duplicate to drop.
    //
    // When BOTH are present the generic row is the MATERIAL — which used to be
    // silently discarded here, because bezelType won and nothing else read
    // `w.bezel`. It now lands on bezelMaterial, which the importer writes to
    // WatchReference.bezel.
    bezelType: clean(w.bezelType) ?? clean(w.bezel),
    bezelMaterial: clean(w.bezelType) ? clean(w.bezel) : undefined,
    // No bezelColors: the pinned shared-types ExtractedWatch has no such field,
    // so the extractor-repo JSON envelope cannot carry one yet. The
    // extractProductData callable DOES extract it — that path is
    // BulkImport's `extracted → entry` map, not this adapter.
    crownType: clean(w.crownType),

    // Dial & hands
    dialColor: clean(w.dialColor),
    dialFinish: clean(w.dialFinish),
    dialFeatures: clean((w as { dialFeatures?: string }).dialFeatures),
    dialIndexes: clean(w.dialIndexes),
    // No dialNumerals here: shared-types' ExtractedWatch (the extractor-repo
    // JSON envelope) has no such field yet, so a scraped "Numerals" row still
    // arrives folded into dialIndexes on THIS path only. The extractProductData
    // callable does split the two.
    dialMaterial: clean(w.dialMaterial),
    hands: clean(w.hands),
    handsColor: clean(w.handsColor),

    // Date complication. An extractor that doesn't emit these leaves all five
    // undefined, which `clean` maps to undefined anyway. dateWheelTextColor is
    // multi-select but arrives already comma-joined and is stored that way, so
    // it needs no more handling here than the four single-select slugs.
    dateDisplay: clean(w.dateDisplay),
    dateWindowPosition: clean(w.dateWindowPosition),
    dateWindowFrame: clean(w.dateWindowFrame),
    dateWheelColor: clean(w.dateWheelColor),
    dateWheelTextColor: clean(w.dateWheelTextColor),

    // Strap
    strapMaterial: clean(w.strapMaterial),
    strapColor: clean(w.strapColor),

    // ScrapedWatchEntry stores complications as one comma-separated string
    // (mapEntryToReference splits it back out and merges with the text-detected
    // and calibre-derived ones).
    functions: w.functions?.length
      ? w.functions.map((f) => f.trim()).filter(Boolean).join(', ') || undefined
      : undefined,

    productionStartYear: production.start,
    productionEndYear: production.end,
  });
}

/**
 * Converts an extractor result envelope into the flat entry array BulkImport
 * consumes. The envelope itself is discarded (see the file header).
 *
 * Watches with a blank `reference` are dropped: the reference is the importer's
 * identity for a non-Shopify doc, so an empty one would mint a garbage ref.
 * The caller is expected to compare `result.watches.length` against the
 * returned length and surface the difference rather than swallow it.
 */
export function extractionResultToEntries(
  result: ExtractionResult,
): ScrapedWatchEntry[] {
  return result.watches
    .filter((w) => typeof w?.reference === 'string' && w.reference.trim() !== '')
    .map(watchToEntry);
}
